import { useState, useRef, useMemo } from 'react';
import {
  Box, Typography, Chip,
  CircularProgress,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, Sync, DeleteOutline } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { tsIntegrationsService, TSIntegration } from '../../services/ts-integrations.service';
import { useModalState } from '../../hooks/useModalState';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import { buildEntityWhere, resolveSelectedIds, emptySelection } from '../../utils/entity-list';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import ConfirmDialog from '../../components/ConfirmDialog';

export default function TSIntegrationsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { setModal } = useModalState('ts-integration');
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection);
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const debouncedSearch = useDebounced(search);
  // Searches `kind`: ts_integrations has no name, and the traffic source's name
  // lives on a joined table this where-builder cannot reach.
  const where = useMemo(
    () => buildEntityWhere('ts_integration', { search: debouncedSearch, statuses: [], tagIds: [] }, ['kind']),
    [debouncedSearch],
  );
  const { gridProps, offset, limit } = useServerPagination(50, where);

  const { data, isFetching } = useQuery({
    queryKey: ['ts-integrations', where, offset, limit],
    queryFn: () => tsIntegrationsService.query({ where, offset, limit }),
    placeholderData: (prev) => prev,
  });

  const rows = useMemo(() => data?.data || [], [data]);
  const selectedIds = useMemo(() => resolveSelectedIds(selection, rows), [selection, rows]);
  const runDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => tsIntegrationsService.delete(id)));
      queryClient.invalidateQueries({ queryKey: ['ts-integrations'] });
      setSelection(emptySelection);
      setConfirmOpen(false);
    } finally { setDeleting(false); }
  };

  const syncMutation = useMutation({
    mutationFn: (id: string) => tsIntegrationsService.syncCost(id),
    onSuccess: (result) => {
      enqueueSnackbar(
        `Synced ${result.synced_campaigns} campaigns, total cost: $${result.total_cost.toFixed(2)}`,
        { variant: 'success' }
      );
      queryClient.invalidateQueries({ queryKey: ['ts-integrations'] });
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Sync failed: ${err.message}`, { variant: 'error' });
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'traffic_source', headerName: 'Traffic Source', flex: 1,
      valueGetter: (_value: unknown, row: TSIntegration) => row.traffic_source?.name || '',
      renderCell: (p: GridRenderCellParams) => p.value || 'N/A',
    },
    {
      field: 'kind', headerName: 'Kind', width: 110,
      renderCell: (p: GridRenderCellParams) => {
        const label = p.value === 'facebook' ? 'Facebook' : p.value === 'google_ads' ? 'Google Ads' : p.value === 'taboola' ? 'Taboola' : p.value;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip label={label} size="small" variant="outlined" />
          </Box>
        );
      },
    },
    {
      field: 'enabled', headerName: 'Enabled', width: 80,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={p.value ? 'Yes' : 'No'} size="small" color={p.value ? 'success' : 'default'} />
        </Box>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={p.value} size="small" color={p.value === 'active' ? 'success' : 'warning'} />
        </Box>
      ),
    },
    {
      field: 'last_sync', headerName: 'Last Sync', width: 160,
      valueGetter: (_value: unknown, row: TSIntegration) => row.info?.sync_cost?.last_run,
      valueFormatter: (v: string | undefined) => v ? new Date(v).toLocaleString() : 'Never',
    },
    {
      field: 'sync_status', headerName: 'Sync Status', width: 100,
      valueGetter: (_value: unknown, row: TSIntegration) => row.info?.sync_cost?.status_code,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value ? (
            <Chip label={p.value} size="small" color={p.value === 'ok' ? 'success' : 'error'} />
          ) : (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton
            size="small"
            title="Sync Cost Now"
            disabled={syncMutation.isPending}
            onClick={() => syncMutation.mutate(params.row.id)}
          >
            {syncMutation.isPending && syncMutation.variables === params.row.id
              ? <CircularProgress size={16} />
              : <Sync sx={{ fontSize: 16 }} />}
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Integrations</Typography>
      </Box>

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          actions: [{ icon: Add, hint: 'Add integration', onClick: () => setModal({ open: true, reason: 'Create' }) }],
          selectedCount: selectedIds.length,
          pluralName: 'integrations',
          selectionActions: [
            { icon: DeleteOutline, hint: 'Delete', onClick: () => setConfirmOpen(true), color: 'inherit' as const },
          ],
        } }}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={`Delete ${selectedIds.length} integration${selectedIds.length === 1 ? '' : 's'}?`}
        body="This cannot be undone. Cost sync for the traffic source stops, and its stored credentials are removed."
        confirmLabel="Delete"
        busyLabel="Deleting..."
        destructive
        busy={deleting}
        onConfirm={runDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
